import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Phone, MessageCircle, Wallet, CalendarClock, Trash2,
  Receipt, ClockAlert, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_BADGE = {
  Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Partially Paid": "bg-blue-100 text-blue-800 border-blue-200",
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Overdue: "bg-red-100 text-red-800 border-red-200",
};

function inr(n) {
  return `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function StatusPill({ status, big }) {
  return (
    <span
      className={`inline-flex text-[11px] uppercase tracking-wider font-semibold ${big ? "px-3 py-1" : "px-2 py-0.5"} rounded-full border ${STATUS_BADGE[status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}
    >
      {status}
    </span>
  );
}

function CollectDialog({ p, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", mode: "Cash", notes: "", date: "", gold_weight_received: 0, silver_weight_received: 0 });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Amount required");
    try {
      await api.post(`/payments/${p.id}/collect`, {
        amount: parseFloat(form.amount),
        mode: form.mode,
        notes: form.notes,
        date: form.date ? new Date(form.date).toISOString() : undefined,
        gold_weight_received: parseFloat(form.gold_weight_received) || 0,
        silver_weight_received: parseFloat(form.silver_weight_received) || 0,
      });
      toast.success("Payment recorded");
      setOpen(false);
      setForm({ amount: "", mode: "Cash", notes: "", date: "", gold_weight_received: 0, silver_weight_received: 0 });
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const isMetalMode = form.mode === "Gold Received" || form.mode === "Silver Received";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="collect-open">
          <Wallet className="w-4 h-4 mr-1.5" /> Record Payment
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Record a Payment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="collect-form">
          <div className="text-sm text-zinc-500">
            Outstanding: <span className="num font-semibold text-zinc-900">{inr(p.remaining_balance)}</span>
          </div>
          <div className="space-y-2">
            <Label>Amount ₹ *</Label>
            <Input type="number" step="0.01" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="collect-amount" required />
          </div>
          <div className="space-y-2">
            <Label>Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger data-testid="collect-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Cash", "UPI", "Bank Transfer", "Cheque", "Card", "Gold Received", "Silver Received", "Adjustment", "Other"].map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isMetalMode && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Gold Received (g)</Label>
                <Input type="number" step="0.001" value={form.gold_weight_received}
                  onChange={(e) => setForm({ ...form, gold_weight_received: e.target.value })}
                  data-testid="collect-gold" />
              </div>
              <div className="space-y-2">
                <Label>Silver Received (g)</Label>
                <Input type="number" step="0.001" value={form.silver_weight_received}
                  onChange={(e) => setForm({ ...form, silver_weight_received: e.target.value })}
                  data-testid="collect-silver" />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label>Date (optional)</Label>
            <Input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="collect-submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PostponeDialog({ p, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) return toast.error("New reminder date required");
    try {
      await api.post(`/payments/${p.id}/postpone`, {
        new_reminder_date: new Date(form.date).toISOString(),
        notes: form.notes,
      });
      toast.success("Reminder postponed");
      setOpen(false);
      setForm({ date: "", notes: "" });
      onDone();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="postpone-open">
          <ClockAlert className="w-4 h-4 mr-1.5" /> Postpone
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Postpone Reminder</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>New Reminder Date *</Label>
            <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="postpone-date" />
          </div>
          <div className="space-y-2">
            <Label>Reason / Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="postpone-submit">Postpone</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  const reload = useCallback(async () => {
    const { data } = await api.get(`/payments/${id}`);
    setP(data);
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  if (!p) return <div className="text-zinc-500">Loading…</div>;

  const digits = (p.mobile || "").replace(/[^0-9]/g, "");
  const paidPct = p.total_amount > 0 ? Math.min(100, (p.amount_paid / p.total_amount) * 100) : 0;

  const doDelete = async () => {
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Payment record deleted");
      navigate("/payments");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setShowDelete(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/payments" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Payments
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={p.status} big />
            {p.invoice_number && <span className="font-mono text-xs text-zinc-500">INV {p.invoice_number}</span>}
            {p.order_number && <span className="font-mono text-xs text-zinc-500">{p.order_number}</span>}
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2">{p.product_name}</h1>
          <div className="text-sm text-zinc-600 mt-1">
            <Link to={`/customers/${p.customer_id}`} className="font-medium hover:underline">{p.customer_name}</Link>
            {p.mobile && <> · <span className="num">{p.mobile}</span></>}
            {p.gold_weight > 0 && <> · {p.gold_weight}g</>}
            {p.due_date && <> · Due <span className="font-medium">{new Date(p.due_date).toLocaleDateString()}</span></>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {p.mobile && (
            <>
              <a href={`tel:${p.mobile}`}><Button size="sm" variant="outline"><Phone className="w-3.5 h-3.5 mr-1" /> Call</Button></a>
              <a href={`https://wa.me/${digits}?text=${encodeURIComponent(`Namaste, this is a friendly reminder that a balance of ${inr(p.remaining_balance)} for ${p.product_name} is pending. Kindly arrange the payment at your earliest convenience. — Om Jewellers`)}`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="text-emerald-600"><MessageCircle className="w-3.5 h-3.5 mr-1" /> Remind on WhatsApp</Button>
              </a>
            </>
          )}
          {p.status !== "Paid" && <CollectDialog p={p} onDone={reload} />}
          {p.status !== "Paid" && <PostponeDialog p={p} onDone={reload} />}
          {user?.role === "admin" && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDelete(true)} data-testid="delete-payment">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Progress + amounts */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="overline text-zinc-500 flex items-center gap-1.5"><Receipt className="w-3 h-3" /> Total</div>
          <div className="font-heading font-black text-3xl mt-2 num">{inr(p.total_amount)}</div>
        </Card>
        <Card className="p-5">
          <div className="overline text-zinc-500 flex items-center gap-1.5"><Wallet className="w-3 h-3" /> Paid</div>
          <div className="font-heading font-black text-3xl mt-2 num text-emerald-700">{inr(p.amount_paid)}</div>
          <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden mt-3">
            <div className="h-full bg-emerald-600 rounded-full transition-all" style={{ width: `${paidPct}%` }} />
          </div>
          <div className="text-xs text-zinc-500 mt-1">{paidPct.toFixed(1)}% collected</div>
        </Card>
        <Card className="p-5">
          <div className="overline text-zinc-500 flex items-center gap-1.5"><IndianRupee className="w-3 h-3" /> Balance</div>
          <div className="font-heading font-black text-3xl mt-2 num text-red-700">{inr(p.remaining_balance)}</div>
        </Card>
      </div>

      {/* Payment history timeline */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Payment History</h3>
          {p.notes && <span className="text-xs text-zinc-500 italic max-w-md truncate">Note: {p.notes}</span>}
        </div>
        {(p.history || []).length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center">No collections yet.</div>
        ) : (
          <ol className="relative border-l border-border ml-3 space-y-4">
            {[...p.history].reverse().map((h) => (
              <li key={h.id} className="ml-6">
                <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-zinc-900 rounded-full border-2 border-white" />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs text-zinc-500">{new Date(h.date).toLocaleString()} · {h.by}</div>
                    <div className="text-sm font-medium">{h.mode}</div>
                    {h.notes && <div className="text-sm text-zinc-600 mt-0.5 whitespace-pre-wrap">{h.notes}</div>}
                  </div>
                  <div className="text-right">
                    <div className="num font-bold text-emerald-700">{inr(h.amount)}</div>
                    <div className="text-xs text-zinc-500">Balance after: {inr(h.balance_after)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this payment record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the record and its history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
